import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { stringify } from 'query-string';
import { isEmpty } from 'lodash-es';
import { JourneyStatus, JourneyStepSummary } from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import i18n from '@/../i18n';
import { store } from '@shared-store/shared-store';
import dayjs from '@/services/tools/dayjs';
import { ShowDialog } from '@/store/dialog/actions';
import { HideToast, ShowToast } from '@/store/toast/actions';
import {
    deleteAutomation,
    getAllAutomations,
    getAutomationByName,
    getAutomationContacts,
    getAutomationDailySummary,
    GetAutomationsParams,
    listFromAutomation,
    loadSingleList,
    loadTemplate,
    previewTemplate,
    updateAutomationStatus,
} from '@/services/ElasticApi';
import { AutomationPaths, ContactsPaths } from '@/services/dictionaries/Paths';
import { DEFAULT_PER_PAGE_COUNT, SHORT_DATE_FORMAT } from '@/services/constants';
import { extractAutomationsStatusCounts } from '@/services/tools/extractAutomationsStatusCounts';
import {
    GetAllAutomationsError,
    GetAllAutomationsStart,
    GetAllAutomationsSuccess,
    GetAutomationByNameError,
    GetAutomationByNameSuccess,
    GetAutomationContactsError,
    GetAutomationContactsSuccess,
    GetAutomationDailySummaryError,
    GetAutomationDailySummarySuccess,
    getAutomationsCountStore,
    ListFromAutomationError,
    ListFromAutomationSuccess,
    LoadPreviewTemplateError,
    LoadPreviewTemplateSuccess,
    UnpickAllAutomations,
    UpdateAutomationStatus,
    UpdateAutomationStatusError,
    UpdateAutomationStatusSuccess,
} from '@/store/automations/actions';
import {
    AutomationsActionTypes,
    AutomationsPagination,
    ContactsListPagination,
    DailySummaryPagination,
    DeleteAutomationAction,
    GetAllAutomationsStartAction,
    GetAutomationByNameStartAction,
    GetAutomationContactsStartAction,
    GetAutomationDailySummaryStartAction,
    ListFromAutomationAction,
    LoadPreviewTemplateAction,
    UpdateAutomationStatusAction,
    UpdateAutomationStatusConfirmationAction,
    UrlSearchParams,
} from '@/store/automations/types';
import { calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';
import { ContactsStartLoading } from '@/store/contacts/actions';
import { ListLoadSingleSuccess } from '@/store/lists/actions';
import { Logger } from '@/services/errors/appLogger';

function* ShowErrorToast(e: any): any {
    Logger.error(e);
    yield put(
        ShowToast({
            severity: 'error',
            message: `Error: ${e?.response?.data?.Error ?? e.message}`,
            duration: 6000,
            onClose: HideToast,
        })
    );
}

function* getAutomations(action: GetAllAutomationsStartAction): any {
    try {
        const { offset = 0, limit = DEFAULT_PER_PAGE_COUNT, from, to, statuses, name } = action.payload;

        const activePage = calculateActivePage(offset, limit);
        const pagination: AutomationsPagination = {
            perPage: limit,
            page: activePage,
            from,
            to,
            statuses,
            name,
        };

        const response = yield call(getAllAutomations, { offset, limit, statuses, from, to, name });

        if (!response) {
            throw new Error('Call getAllAutomations not responding.');
        }

        const collection = response.data;

        const statusCounts = extractAutomationsStatusCounts(response.headers);

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: limit,
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
            ...(statuses ? { statuses } : {}),
            ...(name ? { search: name } : {}),
        };

        yield put(push({ search: stringify(urlQueryParams as any) }));

        yield put(GetAllAutomationsSuccess(collection, statusCounts, pagination));
    } catch (e) {
        yield put(GetAllAutomationsError());
        yield ShowErrorToast(e);
    }
}

function* watchGetAllAutomations(): any {
    yield takeLatest(AutomationsActionTypes.GET_ALL_AUTOMATIONS_START, getAutomations);
}

function* updateAutomationStatusSaga(action: UpdateAutomationStatusAction): any {
    try {
        const { status, name, isEditMode } = action.payload;
        const pagination = yield select((state): any => state.automations.pagination);

        const reloadPageParams: GetAutomationsParams = {
            name: pagination.name,
            limit: pagination.perPage,
            offset: calculateOffset(pagination.page, pagination.perPage),
            from: pagination.from,
            to: pagination.to,
            statuses: pagination.statuses,
        };

        const response = yield call(updateAutomationStatus, name, status);

        const automation = response.data;

        yield put(UpdateAutomationStatusSuccess(automation));

        const infoMessage =
            automation.Status === JourneyStatus.Active ? i18n.t('automationreactivated') : i18n.t('automationpaused');

        yield put(
            ShowToast({
                message: infoMessage,
                severity: 'info',
                duration: 2000,
            })
        );

        if (isEditMode && automation.Status === JourneyStatus.Paused) {
            yield put(push(AutomationPaths.AutomationEditor.replace(':name', safelyEncodeBase64Url(automation.Name))));
        }

        if (!isEditMode) {
            store.dispatch(GetAllAutomationsStart(reloadPageParams));
        }
    } catch (e) {
        yield put(UpdateAutomationStatusError());
        yield ShowErrorToast(e);
    }
}

function* watchUpdateAutomationStatus(): any {
    yield takeLatest(AutomationsActionTypes.UPDATE_AUTOMATION_STATUS, updateAutomationStatusSaga);
}

function* updateAutomationStatusConfirmationSaga(action: UpdateAutomationStatusConfirmationAction): any {
    try {
        const { name, status, isEditMode } = action.payload;
        if (status === JourneyStatus.Active) {
            yield put(
                ShowDialog({
                    title: i18n.t('reactivateautomation'),
                    cancelLabel: i18n.t('cancel'),
                    body: i18n.t('automationreactiveconfirmation', { automationname: name }),
                    confirmLabel: i18n.t('reactivate'),
                    onClickConfirm: (): void => {
                        store.dispatch(UpdateAutomationStatus({ name, status }));
                    },
                })
            );
        } else if (isEditMode) {
            yield put(
                ShowDialog({
                    title: i18n.t('pauseandeditautomation'),
                    cancelLabel: i18n.t('cancel'),
                    body: i18n.t('pauseandeditconfirmation', { automationname: name }),
                    confirmLabel: i18n.t('pauseandedit'),
                    onClickConfirm: (): void => {
                        store.dispatch(UpdateAutomationStatus({ name, status, isEditMode }));
                    },
                })
            );
        } else {
            yield put(
                ShowDialog({
                    title: i18n.t('pauseautomation'),
                    cancelLabel: i18n.t('cancel'),
                    body: i18n.t('automationpauseconfirmation', { automationname: name }),
                    confirmLabel: i18n.t('pause'),
                    onClickConfirm: (): void => {
                        store.dispatch(UpdateAutomationStatus({ name, status }));
                    },
                })
            );
        }
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchupdateAutomationStatusConfirmationSaga(): any {
    yield takeLatest(
        AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_CONFIRMATION,
        updateAutomationStatusConfirmationSaga
    );
}

function* deleteAutomationSaga(action: DeleteAutomationAction): any {
    try {
        yield call(deleteAutomation, action.payload);
        const count = yield select(getAutomationsCountStore);
        const noOfItemsToDelete = action.payload?.Names?.length;
        yield put(
            ShowToast({
                message: i18n.t('automationdeleted', { count: noOfItemsToDelete }),
                severity: 'info',
                duration: 2000,
            })
        );

        const countAfterDelete = count - noOfItemsToDelete;
        const pagination = yield select((state): any => state.automations.pagination);
        const offset = calculateOffset(pagination.page, pagination.perPage, countAfterDelete);
        const defaultStatuses = [JourneyStatus.Invalid, JourneyStatus.Active, JourneyStatus.Paused];

        const params = {
            from: pagination.from ?? null,
            to: pagination.to ?? null,
            statuses: pagination.statuses ?? defaultStatuses,
            limit: pagination.perPage,
            offset,
            name: pagination.name,
        };

        yield put(UnpickAllAutomations());
        yield put(GetAllAutomationsStart(params));
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchDeleteAutomation(): any {
    yield takeLatest(AutomationsActionTypes.DELETE_AUTOMATIONS, deleteAutomationSaga);
}

function* getAutomation(action: GetAutomationByNameStartAction): any {
    try {
        const { name, isTemplateThumbnailsRequired } = action.payload;

        const response = yield call(getAutomationByName, name);

        const singleAutomation = response.data;
        const emailsList = singleAutomation.StepsSummary.reduce(
            (results, step: JourneyStepSummary) => (step.Channel ? [...results, { ...step }] : results),
            []
        );

        if (!isEmpty(emailsList) && isTemplateThumbnailsRequired) {
            for (let i = 0; i < emailsList.length; i++) {
                if (emailsList[i].TemplateName) {
                    const templateData = yield call(loadTemplate, emailsList[i].TemplateName, false);
                    emailsList[i].TemplateData = templateData;
                }
            }
        }

        yield put(GetAutomationByNameSuccess(singleAutomation, emailsList));
    } catch (e) {
        yield put(GetAutomationByNameError());
        yield ShowErrorToast(e);
    }
}

function* watchGetAutomation(): any {
    yield takeLatest(AutomationsActionTypes.GET_AUTOMATION_BY_NAME_START, getAutomation);
}

function* getAutomationDailySummarySaga(action: GetAutomationDailySummaryStartAction): any {
    try {
        const { from, to, channelName, name, dateFilter } = action.payload;

        const dailySummaryPagination: DailySummaryPagination = {
            from,
            to,
            channelName,
        };

        const response = yield call(getAutomationDailySummary, { name, from, to, channelName });

        const singleAutomationDailySummary = response.data;

        const urlQueryParams: UrlSearchParams = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
            ...(channelName ? { email: channelName } : {}),
            ...(dateFilter ? { dateFilter } : {}),
        };

        yield put(push({ search: stringify(urlQueryParams as any) }));

        yield put(GetAutomationDailySummarySuccess(singleAutomationDailySummary, dailySummaryPagination));
    } catch (e) {
        yield put(GetAutomationDailySummaryError());
        yield ShowErrorToast(e);
    }
}

function* watchGetAutomationDailySummary(): any {
    yield takeLatest(AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_START, getAutomationDailySummarySaga);
}

function* getAutomationContactsSaga(action: GetAutomationContactsStartAction): any {
    try {
        const { offset = 0, limit = DEFAULT_PER_PAGE_COUNT, from, to, type, name } = action.payload;

        const activePage = calculateActivePage(offset, limit);
        const pagination: ContactsListPagination = {
            perPage: limit,
            page: activePage,
            from,
            to,
        };

        const response = yield call(getAutomationContacts, { offset, limit, type, from, to, name });

        const collection = response.data;

        const contactsCount = Number(response.headers['x-total-count']);

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: limit,
            ...(from && { from: dayjs(from).format(SHORT_DATE_FORMAT) }),
            ...(to && { to: dayjs(to).format(SHORT_DATE_FORMAT) }),
        };

        yield put(push({ search: stringify(urlQueryParams as any) }));

        yield put(GetAutomationContactsSuccess(collection, pagination, contactsCount));
    } catch (e) {
        yield put(GetAutomationContactsError());
        yield ShowErrorToast(e);
    }
}

function* watchGetAutomationContacts(): any {
    yield takeLatest(AutomationsActionTypes.GET_AUTOMATION_CONTACTS_START, getAutomationContactsSaga);
}

function* LoadPreviewTemplateSaga(action: LoadPreviewTemplateAction): any {
    try {
        const { name } = action.payload;
        const html = yield call(previewTemplate, name);
        const templates = {
            html,
            name,
        };

        yield put(LoadPreviewTemplateSuccess(templates));
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(LoadPreviewTemplateError());
    }
}

function* watchLoadPreviewTemplate(): any {
    yield takeLatest(AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE, LoadPreviewTemplateSaga);
}

function* createListFromAutomation(action: ListFromAutomationAction): any {
    try {
        yield call(listFromAutomation, action.payload.params);

        yield put(ListFromAutomationSuccess());

        const list = yield call(loadSingleList, action.payload.params.listName);

        yield put(ListLoadSingleSuccess(list));

        yield put(
            push(ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(action.payload.params.listName)))
        );

        yield put(ListLoadSingleSuccess(list));

        yield put(
            ContactsStartLoading({
                rule: `List = '${action.payload.params.listName}'`,
                limit: DEFAULT_PER_PAGE_COUNT,
                offset: 0,
            })
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsadded'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListFromAutomationError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchCreateListFromAutomation(): any {
    yield takeLatest(AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION, createListFromAutomation);
}

const automationsSaga = [
    fork(watchGetAllAutomations),
    fork(watchGetAutomation),
    fork(watchGetAutomationDailySummary),
    fork(watchGetAutomationContacts),
    fork(watchDeleteAutomation),
    fork(watchupdateAutomationStatusConfirmationSaga),
    fork(watchUpdateAutomationStatus),
    fork(watchLoadPreviewTemplate),
    fork(watchCreateListFromAutomation),
];

export default automationsSaga;
